/**
 * Partner logins (access levels 4 to 7) and where they may go in the SPA.
 *
 * Suppliers, printers, fulfillment centers and the warehouse sign in at the
 * shared /signin and each lives ONLY in its own portal. The API refuses them
 * everything else (the partner deny guard: only their own `/api/<kind>-portal/**`,
 * `GET /api/me` and `GET /api/platform/**`), so any staff screen would just fire
 * requests that 403. Keeping each partner inside its own root here means none
 * of those screens ever mounts for them.
 *
 * This is routing for usability, not authorization — the API is the gate.
 * Pure (no React, no router) so it is tested directly.
 */

export const ACCESS_LEVEL_CLIENT = 3;
export const ACCESS_LEVEL_SUPPLIER = 4;
export const ACCESS_LEVEL_PRINTER = 5;
export const ACCESS_LEVEL_FC = 6;
export const ACCESS_LEVEL_WAREHOUSE = 7;

export type PartnerKind = "supplier" | "printer" | "fc" | "warehouse";

/** The partners with a printer-flow portal (the supplier portal predates it). */
export type FlowPartnerKind = Exclude<PartnerKind, "supplier">;

export interface PartnerPortalDef {
  accessLevel: number;
  /** The portal root: `/printer` and `/printer/...` belong to it. */
  root: string;
  /** Where the partner lands, and where any other URL sends them. */
  home: string;
  /** The noun in "This account is not linked to a … yet." */
  noun: string;
}

export const PARTNER_PORTALS: Readonly<Record<PartnerKind, PartnerPortalDef>> = {
  supplier: { accessLevel: ACCESS_LEVEL_SUPPLIER, root: "/supplier", home: "/supplier/board", noun: "a supplier" },
  printer: { accessLevel: ACCESS_LEVEL_PRINTER, root: "/printer", home: "/printer/board", noun: "a printer" },
  fc: { accessLevel: ACCESS_LEVEL_FC, root: "/fc", home: "/fc/board", noun: "a fulfillment center" },
  warehouse: { accessLevel: ACCESS_LEVEL_WAREHOUSE, root: "/warehouse", home: "/warehouse/board", noun: "a warehouse" },
};

const PARTNER_KINDS = Object.keys(PARTNER_PORTALS) as PartnerKind[];

/** Where staff land after signing in at /signin (unchanged behaviour). */
export const STAFF_HOME_AFTER_SIGN_IN = "/user-management";

/** Where staff go when they open a partner portal path. */
export const STAFF_HOME_FROM_PORTAL = "/design-requests";

/** Password flows a signed-in partner may still need. */
const PARTNER_ALLOWED_EXACT = ["/signin/check-email", "/signin/reset-password"];

/** Sign-in and password screens: never a place to "return" to after signing in. */
const AUTH_PATH_ROOTS = ["/signin", "/auth", "/login"];

/** The client portal has its own sign-in; /signin never sends anyone there. */
const CLIENT_PORTAL_ROOT = "/client";

/** Where a client lands in the client portal. */
export const CLIENT_HOME = "/client/catalog";

function isUnder(pathname: string, root: string): boolean {
  return pathname === root || pathname.startsWith(`${root}/`);
}

/** The partner a login belongs to, from its access level, or null for staff and clients. */
export function partnerForAccessLevel(accessLevel: number | null | undefined): PartnerKind | null {
  if (accessLevel == null) return null;
  const level = Number(accessLevel);
  return PARTNER_KINDS.find((kind) => PARTNER_PORTALS[kind].accessLevel === level) ?? null;
}

export function isPartnerAccessLevel(accessLevel: number | null | undefined): boolean {
  return partnerForAccessLevel(accessLevel) !== null;
}

/** The portal a path belongs to. `/fc-orders`, `/printers`, `/warehouses/…` are staff paths. */
export function partnerForPortalPath(pathname: string): PartnerKind | null {
  return PARTNER_KINDS.find((kind) => isUnder(pathname, PARTNER_PORTALS[kind].root)) ?? null;
}

export function isPartnerPortalPath(pathname: string): boolean {
  return partnerForPortalPath(pathname) !== null;
}

/** Where to go once /signin succeeds. */
export function homePathAfterSignIn(accessLevel: number | null | undefined): string {
  const partner = partnerForAccessLevel(accessLevel);
  return partner ? PARTNER_PORTALS[partner].home : STAFF_HOME_AFTER_SIGN_IN;
}

/** A login's own home when it opens a portal that is not its own: partner → its board, client → client portal, staff → staff home. */
export function ownHomeFromPortal(accessLevel: number | null | undefined): string {
  const partner = partnerForAccessLevel(accessLevel);
  if (partner) return PARTNER_PORTALS[partner].home;
  return Number(accessLevel) === ACCESS_LEVEL_CLIENT ? CLIENT_HOME : STAFF_HOME_FROM_PORTAL;
}

/**
 * For a signed-in user at `pathname`: where a partner must be sent instead, or
 * null to stay. A partner outside its own root goes home. Anyone who is not a
 * partner always stays (staff on a portal path are sent off by the guard).
 */
export function partnerRedirectFor(pathname: string, accessLevel: number | null | undefined): string | null {
  const partner = partnerForAccessLevel(accessLevel);
  if (!partner) return null;
  if (isUnder(pathname, PARTNER_PORTALS[partner].root)) return null;
  if (PARTNER_ALLOWED_EXACT.includes(pathname)) return null;
  return PARTNER_PORTALS[partner].home;
}

/** The guard's message for a partner login the API does not link to a live partner. */
export function notLinkedMessage(partner: PartnerKind): string {
  return `This account is not linked to ${PARTNER_PORTALS[partner].noun} yet.`;
}

/** A `..` or `.` segment, plain or percent-encoded: the URL parser would resolve it elsewhere. */
const DOT_SEGMENT = /^(?:\.|%2e){1,2}$/i;

/**
 * `from` as a same-origin path, or null when it is anything else.
 *
 * Only `/path[?query][#hash]` is accepted. Rejected: non-strings, `//host`
 * and `/\host` (protocol-relative — browsers treat `\` as `/`), any scheme
 * (`https:`, `javascript:` — none of which start with a single `/`), control
 * characters, and dot segments. This is what stops `state.from` becoming an
 * open redirect.
 */
export function safeReturnPath(from: unknown): string | null {
  if (typeof from !== "string" || from.length === 0 || from.length > 2048) return null;
  if (!from.startsWith("/") || from.startsWith("//") || from.includes("\\")) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(from)) return null;
  const pathname = pathnameOf(from);
  if (pathname.split("/").some((segment) => DOT_SEGMENT.test(segment))) return null;
  return from;
}

/** The path part of a `/path?query#hash` string. */
function pathnameOf(path: string): string {
  const end = path.search(/[?#]/);
  return end === -1 ? path : path.slice(0, end);
}

/**
 * Where /signin sends a user once they are signed in.
 *
 * `from` is the page a guard bounced them off (router `state.from`). It is
 * honoured only when it is a safe same-origin path AND belongs to the user's
 * own side of the app:
 * - a partner returns only into its own root; anything else → its board;
 * - staff return to any staff route, never into a partner root, the client
 *   portal or a sign-in screen; anything else → the usual staff landing.
 */
export function postLoginPath(accessLevel: number | null | undefined, from: unknown): string {
  const home = homePathAfterSignIn(accessLevel);
  const path = safeReturnPath(from);
  if (!path) return home;
  const pathname = pathnameOf(path);

  const partner = partnerForAccessLevel(accessLevel);
  if (partner) {
    return isUnder(pathname, PARTNER_PORTALS[partner].root) ? path : home;
  }

  if (isPartnerPortalPath(pathname)) return home;
  if (isUnder(pathname, CLIENT_PORTAL_ROOT)) return home;
  if (AUTH_PATH_ROOTS.some((root) => isUnder(pathname, root))) return home;
  return path;
}
